import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERMES = "#39FF14";
const OPENCLAW = "#FF2A2A";

const DonutChart = ({ hermesVal, openclawVal, label, testId }) => {
  const data = [
    { name: "Hermes", value: hermesVal },
    { name: "OpenClaw", value: openclawVal },
  ];
  const total = hermesVal + openclawVal;
  const hermesPct = ((hermesVal / total) * 100).toFixed(1);

  return (
    <div data-testid={testId} className="flex flex-col items-center">
      <div className="text-[#475569] text-[0.65rem] tracking-[0.25em] uppercase mb-3">
        {label}
      </div>
      <div className="relative w-[200px] h-[200px] sm:w-[240px] sm:h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="88%"
              paddingAngle={3}
              dataKey="value"
              stroke="none"
              startAngle={90}
              endAngle={-270}
            >
              <Cell fill={HERMES} />
              <Cell fill={OPENCLAW} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span
            className="font-['Unbounded'] text-2xl sm:text-3xl font-black text-[#39FF14]"
          >
            {hermesPct}%
          </span>
          <span className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mt-1">
            Hermes
          </span>
        </div>
      </div>
    </div>
  );
};

const StatBlock = ({ label, hermesVal, openclawVal, testId }) => (
  <div data-testid={testId} className="flex items-center justify-center gap-8 sm:gap-14">
    <div className="text-center">
      <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">
        {label}
      </div>
      <div className="font-['Unbounded'] text-2xl sm:text-3xl font-black" style={{ color: HERMES }}>
        {hermesVal}
      </div>
      <div className="text-[#94A3B8] text-[0.65rem] mt-0.5">Hermes Agent</div>
    </div>
    <div className="text-[#1E293B] text-lg">vs</div>
    <div className="text-center">
      <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">
        {label}
      </div>
      <div className="font-['Unbounded'] text-2xl sm:text-3xl font-black" style={{ color: OPENCLAW }}>
        {openclawVal}
      </div>
      <div className="text-[#94A3B8] text-[0.65rem] mt-0.5">OpenClaw</div>
    </div>
  </div>
);

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/rankings/dominance`)
      .then((res) => setData(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div data-testid="loading-screen" className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="text-[#475569] text-sm tracking-widest uppercase">Loading...</div>
      </div>
    );
  }

  const { today, last_week: lw, totals, meta, computed: c } = data;
  const gaining = c.share_delta > 0;

  return (
    <div className="min-h-screen bg-[#050508] flex flex-col items-center px-4 py-10 sm:py-16">
      {/* Title */}
      <div className="text-[#94A3B8] text-xs sm:text-sm tracking-[0.3em] uppercase mb-3">
        Hermes Agent vs OpenClaw
      </div>
      <h1
        data-testid="app-title"
        className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-[#F8FAFC] text-center"
      >
        Hermes<span className="text-[#39FF14]"> Dominance</span>
      </h1>
      <p data-testid="app-subtitle" className="text-[#475569] text-[0.65rem] tracking-[0.25em] uppercase mt-2 mb-12">
        OpenRouter Top Apps // Daily Token Share
      </p>

      {/* Delta callout */}
      <div
        data-testid="delta-callout"
        className={`flex items-center gap-2 px-5 py-2.5 rounded-full mb-10 border ${
          gaining
            ? "border-[#39FF14]/30 bg-[#39FF14]/5"
            : "border-[#FF2A2A]/30 bg-[#FF2A2A]/5"
        }`}
      >
        <TrendingUp size={14} className={gaining ? "text-[#39FF14]" : "text-[#FF2A2A]"} />
        <span className={`text-sm font-semibold ${gaining ? "text-[#39FF14]" : "text-[#FF2A2A]"}`}>
          Hermes {gaining ? "gained" : "lost"} {Math.abs(c.share_delta)}% share this week
        </span>
        <span className="text-[#475569] text-xs ml-1">
          ({Number(c.hermes_share_lw).toFixed(1)}% → {Number(c.hermes_share_today).toFixed(1)}%)
        </span>
      </div>

      {/* Two pies */}
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10 mb-12">
        <DonutChart
          hermesVal={lw.hermes.tokens_b}
          openclawVal={lw.openclaw.tokens_b}
          label="Last Week"
          testId="pie-last-week"
        />

        <div data-testid="arrow-indicator" className="flex flex-col items-center gap-1">
          <ArrowRight size={28} className={gaining ? "text-[#39FF14]" : "text-[#475569]"} />
          <span
            className={`font-['Unbounded'] text-lg font-bold ${
              gaining ? "text-[#39FF14]" : "text-[#FF2A2A]"
            }`}
          >
            {gaining ? "+" : ""}{c.share_delta}%
          </span>
        </div>

        <DonutChart
          hermesVal={today.hermes.tokens_b}
          openclawVal={today.openclaw.tokens_b}
          label="Today"
          testId="pie-today"
        />
      </div>

      {/* Token numbers */}
      <div className="w-full max-w-2xl space-y-8">
        <StatBlock
          testId="stat-today"
          label="Today"
          hermesVal={today.hermes.label}
          openclawVal={today.openclaw.label}
        />
        <StatBlock
          testId="stat-last-week"
          label="Last Week"
          hermesVal={lw.hermes.label}
          openclawVal={lw.openclaw.label}
        />

        {/* Growth row */}
        <div data-testid="growth-row" className="flex items-center justify-center gap-8 sm:gap-14">
          <div className="text-center">
            <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">
              7D Growth
            </div>
            <div className={`font-['Unbounded'] text-xl font-bold ${c.hermes_growth_pct > 0 ? "text-[#39FF14]" : "text-[#FF2A2A]"}`}>
              {c.hermes_growth_pct > 0 ? "+" : ""}{c.hermes_growth_pct}%
            </div>
            <div className="text-[#94A3B8] text-[0.65rem] mt-0.5">Hermes Agent</div>
          </div>
          <div className="text-[#1E293B] text-lg">vs</div>
          <div className="text-center">
            <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">
              7D Growth
            </div>
            <div className={`font-['Unbounded'] text-xl font-bold ${c.openclaw_growth_pct > 0 ? "text-[#39FF14]" : "text-[#FF2A2A]"}`}>
              {c.openclaw_growth_pct > 0 ? "+" : ""}{c.openclaw_growth_pct}%
            </div>
            <div className="text-[#94A3B8] text-[0.65rem] mt-0.5">OpenClaw</div>
          </div>
        </div>

        {/* Totals muted */}
        <div data-testid="totals-row" className="flex items-center justify-center gap-8 sm:gap-14 pt-4 border-t border-[#1E293B]/50">
          <div className="text-center">
            <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">All Time</div>
            <div className="text-[#475569] text-base">{totals.hermes}</div>
          </div>
          <div className="text-[#0C0C12]">|</div>
          <div className="text-center">
            <div className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase mb-1">All Time</div>
            <div className="text-[#475569] text-base">{totals.openclaw}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer data-testid="app-footer" className="mt-14">
        <p className="text-[#475569] text-[0.55rem] tracking-[0.2em] uppercase">
          Source:{" "}
          <a href="https://openrouter.ai/rankings#apps" target="_blank" rel="noopener noreferrer" className="hover:text-[#94A3B8]">
            openrouter.ai/rankings
          </a>
          {" "}// Refreshed daily
        </p>
      </footer>
    </div>
  );
}

export default App;
